/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import OcChangeLogDialog from "../../components/purchase-orders/OcChangeLogDialog";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUser } from "../../store/slices/session/sessionSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useGetOcClientsQuery,
  useGetOcIssuersQuery,
  useGetOcOrdersQuery,
  useGetOcCreatorsQuery,
  useLazyGetOcOrderByIdQuery,
  useDeleteOcOrderMutation,
} from "../../store/apis/ocApi";
import { useUserRole } from "../../hooks/useUserRole";
import { OcApiMode, OcOrderListItem } from "../../types/oc";
import { formatEUR, formatDateES } from "../../utils/oc/ocCalc";
import { generateOcPdf } from "../../utils/oc/PDFGenerator";
import { buildPdfArgsFromDetail } from "../../utils/oc/buildPdfArgs";
import ClientFilterSelector from "../../components/common/ClientFilterSelector";
import { exportOcOrdersCsv } from "../../utils/oc/exportOcCsv";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import {
  OC_CARD_SX,
  ocTableHeadSx,
} from "../../components/purchase-orders/ocStyles";

const API_MODES: OcApiMode[] = [
  "OC_MEDIOS",
  "OC_CREATIVIDADES",
  "OC_PROVEEDORES",
  "OC_PROVEEDORES_INCLUYE",
];

const PurchaseOrderHistory: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const collapsed = useSelector(selectSidebarCollapsed);
  const user = useSelector(selectUser);
  const { isAdmin } = useUserRole();

  // ---- Filtros ----
  const [clientId, setClientId] = useState("");
  const [issuerCompanyId, setIssuerCompanyId] = useState("");
  const [mode, setMode] = useState<OcApiMode | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [createdByUserId, setCreatedByUserId] = useState("");

  // ---- Paginación (MUI page es 0-indexed; el backend espera 1-indexed) ----
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // ---- Orden de columnas (client-side: el backend de /oc/orders no acepta
  // sortBy/sortOrder, así que solo reordena la página ya cargada). Por
  // defecto, más reciente a más antigua.
  type SortKey =
    | "ocNumber"
    | "ocDate"
    | "clientLabel"
    | "issuerLabel"
    | "mode"
    | "total"
    | "createdByName"
    | "createdAt"
    | "updatedByName";
  const [sortKey, setSortKey] = useState<SortKey>("ocDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const { data: clientsResp } = useGetOcClientsQuery();
  const { data: issuersResp } = useGetOcIssuersQuery();
  const clients = useMemo(() => clientsResp?.data ?? [], [clientsResp]);
  const issuers = useMemo(() => issuersResp?.data ?? [], [issuersResp]);
  const { data: creatorsResp } = useGetOcCreatorsQuery();
  const creators = useMemo(() => creatorsResp?.data ?? [], [creatorsResp]);
  // El catálogo llega por razón social; el filtro opera a nivel empresa, así que
  // deduplicamos por companyId (el filtro envía clientId = companyId).
  const clientOptions = useMemo(() => {
    const seen = new Map<string, { id: string; label: string }>();
    for (const c of clients) {
      if (!seen.has(c.companyId)) {
        seen.set(c.companyId, { id: c.companyId, label: c.companyLabel });
      }
    }
    return Array.from(seen.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "es")
    );
  }, [clients]);

  const { data: ordersResp, isFetching } = useGetOcOrdersQuery({
    page: page + 1,
    pageSize,
    clientId: clientId || undefined,
    issuerCompanyId: issuerCompanyId || undefined,
    mode: mode || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    search: search || undefined,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
    createdByUserId: createdByUserId || undefined,
  });

  // Memoizado porque alimenta el useMemo de ordenación: el `?? []` crearía un
  // array nuevo en cada render y lo recalcularía siempre.
  const orders = useMemo(
    () => ordersResp?.data?.items ?? [],
    [ordersResp?.data?.items]
  );
  const total = ordersResp?.data?.total ?? 0;

  const sortedOrders = useMemo(() => {
    const getSortValue = (
      o: OcOrderListItem,
      key: SortKey
    ): string | number => {
      switch (key) {
        case "ocNumber":
          return o.ocNumber ?? "";
        case "ocDate":
          return o.ocDate ? new Date(o.ocDate).getTime() : 0;
        case "clientLabel":
          return o.clientLabel ?? "";
        case "issuerLabel":
          return o.issuerLabel ?? "";
        case "mode":
          return t(`oc.modeShort.${o.mode}`) ?? "";
        case "total":
          return o.total ?? 0;
        case "createdByName":
          return o.createdByName ?? "";
        case "createdAt":
          return o.createdAt ? new Date(o.createdAt).getTime() : "";
        case "updatedByName":
          return o.updatedByName ?? "";
      }
    };

    return [...orders].sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);

      // Filas sin valor (p.ej. "Modificado por" vacío) siempre al final,
      // sea cual sea la dirección del orden.
      const aEmpty = va === "" || va == null;
      const bEmpty = vb === "" || vb == null;
      if (aEmpty && !bEmpty) return 1;
      if (bEmpty && !aEmpty) return -1;
      if (aEmpty && bEmpty) return 0;

      const cmp =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), undefined, {
              sensitivity: "base",
            });

      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [orders, sortKey, sortDirection, t]);

  const [fetchOrderById, { isFetching: isDownloading }] =
    useLazyGetOcOrderByIdQuery();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const resp = await fetchOrderById(id).unwrap();
      await generateOcPdf(buildPdfArgsFromDetail(resp.data));
    } catch (e: any) {
      dispatch(
        setToastMessage({
          message: e?.data?.message || t("oc.history.pdfError"),
          severity: "error",
        })
      );
    } finally {
      setDownloadingId(null);
    }
  };

  // ---- Exportar CSV (reusa los filtros activos, sin paginación) ----
  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = async () => {
    if (!user?.token) return;
    setIsExporting(true);
    try {
      await exportOcOrdersCsv(
        {
          clientId: clientId || undefined,
          issuerCompanyId: issuerCompanyId || undefined,
          mode: mode || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          search: search || undefined,
          createdFrom: createdFrom || undefined,
          createdTo: createdTo || undefined,
          createdByUserId: createdByUserId || undefined,
        },
        user.token
      );
    } catch (e: any) {
      dispatch(
        setToastMessage({
          message: e?.message || t("oc.history.csvError"),
          severity: "error",
        })
      );
    } finally {
      setIsExporting(false);
    }
  };

  // ---- Eliminar OC (solo Admin) ----
  const [deleteOcOrder, { isLoading: isDeleting }] = useDeleteOcOrderMutation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteOcOrder(confirmDeleteId).unwrap();
      dispatch(
        setToastMessage({
          message: t("oc.history.deleted"),
          severity: "success",
        })
      );
    } catch (e: any) {
      dispatch(
        setToastMessage({
          message: e?.data?.message || t("oc.history.deleteError"),
          severity: "error",
        })
      );
    } finally {
      setConfirmDeleteId(null);
    }
  };

  // ---- Histórico de cambios (quién modificó qué y cuándo) ----
  const [changeLogFor, setChangeLogFor] = useState<OcOrderListItem | null>(
    null
  );

  const clearFilters = () => {
    setClientId("");
    setIssuerCompanyId("");
    setMode("");
    setStartDate("");
    setEndDate("");
    setSearch("");
    setCreatedFrom("");
    setCreatedTo("");
    setCreatedByUserId("");
    setPage(0);
  };

  const hasFilters =
    clientId ||
    issuerCompanyId ||
    mode ||
    startDate ||
    endDate ||
    search ||
    createdFrom ||
    createdTo ||
    createdByUserId;

  // 10 columnas de datos + la columna "Acciones", siempre visible (editar / cambios)
  const colCount = 11;

  // Fecha y hora legibles para la última modificación.
  const formatWhen = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h3">{t("oc.history.title")}</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={
                isExporting ? <CircularProgress size={18} /> : <DownloadIcon />
              }
              onClick={handleExportCsv}
              disabled={isExporting}
            >
              {t("oc.history.downloadCsv")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/purchase-orders")}
            >
              {t("oc.history.newOc")}
            </Button>
          </Box>
        </Box>

        {/* Filtros */}
        <Paper elevation={0} sx={{ ...OC_CARD_SX, mb: 3 }}>
          {/* Fila 1 — el selector de cliente lleva la etiqueta encima del
              control, así que va solo: mezclado con los campos de etiqueta
              flotante quedaba desalineado. Contenedor propio para forzar el
              salto de fila sin tener que estirarlo a las 12 columnas. */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="pSmallest"
                sx={{ mb: 0.5, display: "block" }}
                color={theme.palette.neutral["600"]}
              >
                {t("oc.history.filterClient")}
              </Typography>
              <ClientFilterSelector
                options={clientOptions}
                value={clientId}
                allLabel={t("oc.history.all")}
                onChange={(id) => {
                  setClientId(id);
                  setPage(0);
                }}
              />
            </Grid>
          </Grid>

          {/* Fila 2 — campos con etiqueta flotante. Las fechas van a ancho
              fijo (solo necesitan dd/mm/aaaa) y la búsqueda absorbe el
              espacio sobrante con `md` sin valor. */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("oc.history.filterIssuer")}
                fullWidth
                size="small"
                value={issuerCompanyId}
                onChange={(e) => {
                  setIssuerCompanyId(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("oc.history.allFem")}</MenuItem>
                {issuers.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("oc.history.filterMode")}
                fullWidth
                size="small"
                value={mode}
                onChange={(e) => {
                  setMode(e.target.value as OcApiMode | "");
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("oc.history.all")}</MenuItem>
                {API_MODES.map((m) => (
                  <MenuItem key={m} value={m}>
                    {t(`oc.modeShort.${m}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md>
              <TextField
                label={t("oc.history.searchPlaceholder")}
                fullWidth
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md="auto">
              <TextField
                label={t("oc.history.from")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", md: 170 } }}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md="auto">
              <TextField
                label={t("oc.history.to")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", md: 170 } }}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
          </Grid>

          {/* Fila 3 — quién y cuándo creó la OC en la plataforma (no la fecha
              de la OC, que es la de la fila de arriba). */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("oc.history.filterCreatedBy")}
                fullWidth
                size="small"
                value={createdByUserId}
                onChange={(e) => {
                  setCreatedByUserId(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">{t("oc.history.all")}</MenuItem>
                {creators.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md="auto">
              <TextField
                label={t("oc.history.createdFrom")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", md: 170 } }}
                value={createdFrom}
                onChange={(e) => {
                  setCreatedFrom(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md="auto">
              <TextField
                label={t("oc.history.createdTo")}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", md: 170 } }}
                value={createdTo}
                onChange={(e) => {
                  setCreatedTo(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            {hasFilters && (
              <Grid
                item
                xs={12}
                md="auto"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Button onClick={clearFilters}>
                  {t("oc.history.clearFilters")}
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Tabla */}
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small" sx={ocTableHeadSx}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "ocNumber"}
                      direction={sortKey === "ocNumber" ? sortDirection : "asc"}
                      onClick={() => handleSort("ocNumber")}
                    >
                      {t("oc.history.colNumber")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "ocDate"}
                      direction={sortKey === "ocDate" ? sortDirection : "asc"}
                      onClick={() => handleSort("ocDate")}
                    >
                      {t("oc.history.colDate")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "clientLabel"}
                      direction={
                        sortKey === "clientLabel" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("clientLabel")}
                    >
                      {t("oc.history.colClient")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "issuerLabel"}
                      direction={
                        sortKey === "issuerLabel" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("issuerLabel")}
                    >
                      {t("oc.history.colIssuer")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "mode"}
                      direction={sortKey === "mode" ? sortDirection : "asc"}
                      onClick={() => handleSort("mode")}
                    >
                      {t("oc.history.colMode")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "total"}
                      direction={sortKey === "total" ? sortDirection : "asc"}
                      onClick={() => handleSort("total")}
                    >
                      {t("oc.history.colTotal")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "createdByName"}
                      direction={
                        sortKey === "createdByName" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("createdByName")}
                    >
                      {t("oc.history.colCreatedBy")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "createdAt"}
                      direction={sortKey === "createdAt" ? sortDirection : "asc"}
                      onClick={() => handleSort("createdAt")}
                    >
                      {t("oc.history.colCreatedAt")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "updatedByName"}
                      direction={
                        sortKey === "updatedByName" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("updatedByName")}
                    >
                      {t("oc.history.colUpdatedBy")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">{t("oc.history.colPdf")}</TableCell>
                  <TableCell align="center">
                    {t("oc.history.colActions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                      <OrbLoader
                        size="large"
                        label={t("common.loading", {
                          defaultValue: "Cargando…",
                        })}
                      />
                    </TableCell>
                  </TableRow>
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                      <Typography
                        variant="pSmall"
                        color={theme.palette.neutral["600"]}
                      >
                        {t("oc.history.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedOrders.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>{o.ocNumber}</TableCell>
                      <TableCell>{formatDateES(o.ocDate)}</TableCell>
                      <TableCell>{o.clientLabel}</TableCell>
                      <TableCell>{o.issuerLabel}</TableCell>
                      <TableCell>{t(`oc.modeShort.${o.mode}`)}</TableCell>
                      <TableCell align="right">{formatEUR(o.total)}</TableCell>
                      <TableCell>{o.createdByName || "—"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatWhen(o.createdAt) || "—"}
                      </TableCell>
                      <TableCell>
                        {o.updatedByName ? (
                          <Tooltip title={formatWhen(o.updatedAt)}>
                            <span>{o.updatedByName}</span>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t("oc.history.downloadPdf")}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={isDownloading && downloadingId === o.id}
                              onClick={() => handleDownload(o.id)}
                            >
                              {isDownloading && downloadingId === o.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t("oc.history.editOc")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(`/purchase-orders/${o.id}/edit`)
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t("oc.changelog.button")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setChangeLogFor(o)}
                            >
                              <ManageHistoryIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isAdmin && (
                          <Tooltip title={t("oc.history.deleteOc")}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={isDeleting}
                                onClick={() => setConfirmDeleteId(o.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t("common.rowsPerPage")}
          />
        </Paper>
      </Box>

      <OcChangeLogDialog
        open={changeLogFor !== null}
        ocId={changeLogFor?.id ?? null}
        ocNumber={changeLogFor?.ocNumber}
        onClose={() => setChangeLogFor(null)}
      />

      <ConfirmationModal
        open={confirmDeleteId !== null}
        title={t("oc.history.deleteTitle")}
        message={t("oc.history.deleteMessage")}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default PurchaseOrderHistory;
